import React, { useState, useEffect } from 'react';
import { Container, Grid, Typography, Pagination, Alert, Box, Paper, Button } from '@mui/material';
import { getUserListings, deleteRoom, updateRoomStatus } from '../services/api';
import RoomManageCard from '../components/room/RoomManageCard';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useNotification } from '../contexts/NotificationContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home as HomeIcon, Add as AddIcon } from '@mui/icons-material';

const MyRooms = () => {
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const limit = 12;
    const { showNotification } = useNotification();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        fetchRooms();
    }, [page]);

    useEffect(() => {
        // ナビゲーション時のメッセージを表示
        if (location.state?.message) {
            showNotification(location.state.message, location.state.severity || 'info');
            // メッセージを表示したら状態をクリア
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location, showNotification, navigate]);

    const fetchRooms = async () => {
        try {
            setLoading(true);
            const data = await getUserListings(page, limit);
            setRooms(data.rooms);
            setTotalPages(data.totalPages);
        } catch (error) {
            console.error('物件取得エラー:', error);
            showNotification('物件の取得に失敗しました', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (roomId) => {
        try {
            await deleteRoom(roomId);
            showNotification('物件を削除しました', 'success');
            fetchRooms();
        } catch (error) {
            console.error('物件削除エラー:', error);
            showNotification('物件の削除に失敗しました', 'error');
        }
    };

    const handleStatusUpdate = async (roomId, status) => {
        try {
            await updateRoomStatus(roomId, status);
            showNotification('物件の状態を更新しました', 'success');
            fetchRooms();
        } catch (error) {
            if (error.response?.data?.error === 'LISTING_LIMIT_EXCEEDED') {
                showNotification('物件の登録数が上限（3件）に達しています', 'error');
            } else {
                showNotification('物件の状態更新に失敗しました', 'error');
            }
        }
    };

    const handleRoomClick = (roomId) => {
        navigate(`/rooms/${roomId}`);
    };

    if (loading) return <LoadingSpinner />;

    // 空の状態表示コンポーネント
    const EmptyState = () => (
        <Paper
            elevation={0}
            sx={{
                textAlign: 'center',
                py: 8,
                px: 4,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                border: '1px solid',
                borderColor: 'grey.200'
            }}
        >
            <Box
                sx={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #3498db 0%, #5dade2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    boxShadow: '0 8px 32px rgba(52, 152, 219, 0.3)'
                }}
            >
                <HomeIcon sx={{ fontSize: 60, color: 'white' }} />
            </Box>
            <Typography
                variant="h5"
                component="h2"
                gutterBottom
                sx={{
                    fontWeight: 'bold',
                    color: 'text.primary',
                    mb: 2
                }}
            >
                まだ物件が登録されていません
            </Typography>
            <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}
            >
                あなたの素敵な物件を登録して、理想のルームメイトを見つけませんか？
                物件登録は簡単で、数分で完了します。
            </Typography>
            <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => navigate('/create-listing')}
                sx={{
                    borderRadius: 20,
                    px: 4,
                    py: 1.5,
                    background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                    boxShadow: '0 4px 16px rgba(52, 152, 219, 0.4)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #2980b9 0%, #1f5f8b 100%)',
                        boxShadow: '0 6px 20px rgba(52, 152, 219, 0.5)',
                        transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
            >
                物件を登録する
            </Button>
        </Paper>
    );

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                登録した物件
            </Typography>
            {location.state?.message && (
                <Box mb={3}>
                    <Alert severity={location.state.severity || 'info'}>
                        {location.state.message}
                    </Alert>
                </Box>
            )}

            {rooms.length === 0 ? (
                <EmptyState />
            ) : (
                <>
                    <Grid container spacing={3}>
                        {rooms.map((room) => (
                            <Grid item xs={12} sm={6} md={4} key={room._id}>
                                <RoomManageCard
                                    room={room}
                                    onUpdate={fetchRooms}
                                    onDelete={handleDelete}
                                    onStatusUpdate={handleStatusUpdate}
                                    onCardClick={() => handleRoomClick(room._id)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    {totalPages > 1 && (
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}
                        />
                    )}
                </>
            )}
        </Container>
    );
};

export default MyRooms; 